import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import {
  ShoppingCart,
  CreditCard,
  Star,
  Check,
  Zap,
  Users,
  Globe,
  Wallet,
  TrendingUp,
  Award,
  Loader,
  AlertCircle,
  ExternalLink
} from 'lucide-react';
import API_ENDPOINTS from '../../config/api';

const ProductCatalog = () => {
  const [products, setProducts] = useState({
    all_products: [],
    membership_products: [],
    credit_products: []
  });
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [activeTab, setActiveTab] = useState('membership');

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.PUBLIC}/products`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      } else {
        toast.error('Failed to load products');
      }
    } catch (error) {
      console.error('Error loading products:', error);
      toast.error('Error loading products');
    } finally {
      setLoading(false);
    }
  };

  const purchaseProduct = async (product) => {
    const user = JSON.parse(localStorage.getItem('user'));
    
    // Check if user has wallet address
    if (!user.wallet_address) {
      toast.error('Please set your wallet address first in the Wallet section');
      return;
    }

    setPurchasing(product.id);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.PUBLIC}/payments/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          member_id: user.id,
          product_id: product.id,
          currency: 'usdttrc20'
        })
      });

      if (response.ok) {
        const paymentData = await response.json();
        
        // Show payment instructions
        const paymentWindow = window.open(
          paymentData.payment_url || '', 
          '_blank',
          'width=600,height=800,scrollbars=yes,resizable=yes'
        );

        toast.success('Payment created! Complete the payment in the new window.');
        
        // You could also redirect to a payment status page
        // window.location.href = `/payment/${paymentData.payment_id}`;
        
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create payment');
      }
    } catch (error) {
      console.error('Error creating payment:', error);
      toast.error('Error creating payment');
    } finally {
      setPurchasing(null);
    }
  };

  const getTierColor = (tier) => {
    switch(tier) {
      case 'basic': return 'bg-blue-100 text-blue-800';
      case 'pro': return 'bg-purple-100 text-purple-800';
      case 'enterprise': return 'bg-gold-100 text-gold-800 bg-gradient-to-r from-yellow-400 to-orange-500 text-white';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTierIcon = (tier) => {
    switch(tier) {
      case 'basic': return <Star className="w-4 h-4" />;
      case 'pro': return <TrendingUp className="w-4 h-4" />;
      case 'enterprise': return <Award className="w-4 h-4" />;
      default: return <ShoppingCart className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Product Catalog</h1>
          <p className="text-gray-600 mt-2">
            Choose your membership level or purchase advertising credits
          </p>
        </div>
      </div>

      {/* Important Notice */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-medium text-yellow-800">Payment Information</h4>
            <div className="text-sm text-yellow-700 mt-1 space-y-1">
              <p>• All payments are <strong>member-to-member</strong> using cryptocurrency</p>
              <p>• Payments go directly to other members' wallets (non-custodial)</p>
              <p>• Matrix system automatically routes payments based on positions</p>
              <p>• Every 5th payment goes to admin wallet for system maintenance</p>
              <p>• Set your wallet address in the <strong>Wallet</strong> section before purchasing</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('membership')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'membership'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            Membership Levels ({products.membership_products.length})
          </button>
          <button
            onClick={() => setActiveTab('credits')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'credits'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Zap className="w-4 h-4 inline mr-2" />
            Ad Credits ({products.credit_products.length})
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'membership' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.membership_products.map((product) => (
            <Card key={product.id} className={`relative overflow-hidden ${
              product.tier === 'enterprise' ? 'border-2 border-yellow-400 shadow-lg' : 'hover:shadow-lg'
            } transition-shadow`}>
              {product.tier === 'enterprise' && (
                <div className="absolute top-0 right-0 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                  MOST POPULAR
                </div>
              )}
              
              <CardHeader className="text-center pb-2">
                <div className="flex items-center justify-center mb-4">
                  <div className={`p-3 rounded-full ${
                    product.tier === 'enterprise' ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white' : 'bg-gray-100'
                  }`}>
                    {getTierIcon(product.tier)}
                  </div>
                </div>
                
                <CardTitle className="text-xl font-bold">{product.name}</CardTitle>
                <CardDescription className="text-sm">{product.description}</CardDescription>
                
                <div className="mt-4">
                  <div className="text-3xl font-bold text-gray-900">
                    ${product.price}
                    <span className="text-base font-normal text-gray-500">/one-time</span>
                  </div>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mt-2 ${getTierColor(product.tier)}`}>
                    {getTierIcon(product.tier)}
                    <span className="ml-1">{product.tier.toUpperCase()}</span>
                  </span>
                </div>
              </CardHeader>
              
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="text-center">
                      <div className="font-semibold text-lg text-blue-600">{product.credits_included}</div>
                      <div className="text-gray-500">Ad Credits</div>
                    </div>
                    <div className="text-center">
                      <div className="font-semibold text-lg text-green-600">{product.matrix_positions}</div>
                      <div className="text-gray-500">Matrix Positions</div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <h4 className="font-medium text-gray-900">Features:</h4>
                    <ul className="space-y-1">
                      {product.features.map((feature, index) => (
                        <li key={index} className="flex items-start text-sm text-gray-600">
                          <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <Button
                    onClick={() => purchaseProduct(product)}
                    disabled={purchasing === product.id}
                    className={`w-full ${
                      product.tier === 'enterprise' 
                        ? 'bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white'
                        : ''
                    }`}
                  >
                    {purchasing === product.id ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin mr-2" />
                        Creating Payment...
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        Purchase Now
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {activeTab === 'credits' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.credit_products.map((product) => (
            <Card key={product.id} className="hover:shadow-lg transition-shadow">
              <CardHeader className="text-center pb-2">
                <div className="flex items-center justify-center mb-4">
                  <div className="p-3 rounded-full bg-blue-100">
                    <Zap className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                
                <CardTitle className="text-xl font-bold">{product.name}</CardTitle>
                <CardDescription className="text-sm">{product.description}</CardDescription>
                
                <div className="mt-4">
                  <div className="text-3xl font-bold text-gray-900">
                    ${product.price}
                  </div>
                  <div className="text-lg font-semibold text-blue-600 mt-1">
                    {product.credits_included} Credits
                  </div>
                  <div className="text-sm text-gray-500">
                    ${(product.price / product.credits_included).toFixed(3)} per credit
                  </div>
                </div>
              </CardHeader>
              
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <h4 className="font-medium text-gray-900">Use credits for:</h4>
                    <ul className="space-y-1">
                      {product.features.map((feature, index) => (
                        <li key={index} className="flex items-start text-sm text-gray-600">
                          <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <Button
                    onClick={() => purchaseProduct(product)}
                    disabled={purchasing === product.id}
                    className="w-full"
                    variant="outline"
                  >
                    {purchasing === product.id ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin mr-2" />
                        Creating Payment...
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="w-4 h-4 mr-2" />
                        Buy Credits
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Help Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            Need Help?
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Payment Process:</h4>
              <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600">
                <li>Set your wallet address in the Wallet section</li>
                <li>Choose a product and click "Purchase Now"</li>
                <li>Complete the crypto payment using the instructions</li>
                <li>Your credits and positions are activated automatically</li>
              </ol>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Matrix System:</h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                <li>Payments route to other members automatically</li>
                <li>Your sponsor and upline benefit from your purchases</li>
                <li>Auto-repurchase keeps you cycling continuously</li>
                <li>Every 5th payment supports system development</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProductCatalog;